import { useState, useRef } from "react"
import { 
  Folder, 
  FolderPlus, 
  Upload, 
  Image as ImageIcon, 
  ArrowLeft, 
  MoreVertical,
  Trash2,
  Eye,
  ChevronRight,
  AlertCircle
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  useEventGalleryFolders,
  useEventGalleryImages,
  useCreateFolder,
  useDeleteFolder,
  useUploadImage,
  useDeleteImage,
  type EventGalleryFolder,
  type EventGalleryImage,
} from "@/hooks/useEventGallery"
import { GalleryImage } from "./gallery/GalleryImage"
import { ImagePreviewModal } from "./gallery/ImagePreviewModal"
import { formatFileSize } from "@/lib/utils/formatFileSize"

interface EventGalleryProps {
  eventId: string
}

interface BreadcrumbItem {
  id: string | null
  name: string
}

interface UploadProgress {
  file: File
  progress: number
  status: 'uploading' | 'completed' | 'error'
}

export function EventGallery({ eventId }: EventGalleryProps) {
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: null, name: "Gallery" }
  ])
  const [newFolderName, setNewFolderName] = useState("")
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<EventGalleryImage | null>(null)
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: folders, isLoading: foldersLoading } = useEventGalleryFolders(eventId, currentFolderId)
  const { data: images, isLoading: imagesLoading } = useEventGalleryImages(eventId, currentFolderId)
  const createFolderMutation = useCreateFolder()
  const deleteFolderMutation = useDeleteFolder()
  const uploadImageMutation = useUploadImage()
  const deleteImageMutation = useDeleteImage()

  console.log('EventGallery render:', {
    eventId,
    currentFolderId,
    foldersCount: folders?.length || 0,
    imagesCount: images?.length || 0,
    isLoading: foldersLoading || imagesLoading
  })

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return

    await createFolderMutation.mutateAsync({
      eventId,
      name: newFolderName.trim(),
      parentFolderId: currentFolderId,
    })

    setNewFolderName("")
    setIsCreateFolderOpen(false)
  }

  const handleFolderClick = (folder: EventGalleryFolder) => {
    setCurrentFolderId(folder.id)
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }])
  }

  const handleBreadcrumbClick = (index: number) => {
    const targetItem = breadcrumbs[index]
    setCurrentFolderId(targetItem.id)
    setBreadcrumbs(breadcrumbs.slice(0, index + 1))
  }

  const handleBackClick = () => {
    if (breadcrumbs.length > 1) {
      const newBreadcrumbs = breadcrumbs.slice(0, -1)
      const parentItem = newBreadcrumbs[newBreadcrumbs.length - 1]
      setCurrentFolderId(parentItem.id)
      setBreadcrumbs(newBreadcrumbs)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    files.forEach(handleFileUpload)
    event.target.value = "" // Reset input
  }

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      return
    }

    const uploadId = `${Date.now()}-${Math.random()}`
    
    setUploadProgress(prev => ({
      ...prev,
      [uploadId]: { file, progress: 0, status: 'uploading' }
    }))

    try {
      await uploadImageMutation.mutateAsync({
        eventId,
        folderId: currentFolderId,
        file,
        onProgress: (progress) => {
          setUploadProgress(prev => ({
            ...prev,
            [uploadId]: { ...prev[uploadId], progress }
          }))
        }
      })

      setUploadProgress(prev => ({
        ...prev,
        [uploadId]: { ...prev[uploadId], status: 'completed' }
      }))

      // Remove from progress after 2 seconds
      setTimeout(() => {
        setUploadProgress(prev => {
          const { [uploadId]: removed, ...rest } = prev
          return rest
        })
      }, 2000)
    } catch (error) {
      setUploadProgress(prev => ({
        ...prev,
        [uploadId]: { ...prev[uploadId], status: 'error' }
      }))
    }
  }

  const handleDeleteFolder = async (folderId: string) => {
    await deleteFolderMutation.mutateAsync({ folderId, eventId })
  }

  const handleDeleteImage = async (image: EventGalleryImage) => {
    await deleteImageMutation.mutateAsync({
      imageId: image.id,
      filePath: image.file_path,
      eventId,
      folderId: currentFolderId,
    })
    setSelectedImage(null)
  }



  const isLoading = foldersLoading || imagesLoading

  return (
    <div className="space-y-6">
      {/* Authentication Warning */}
      {(!folders?.length && !images?.length && !isLoading) && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            If you're having trouble viewing images, please make sure you're logged in. 
            Event gallery images are private and require authentication to access.
          </AlertDescription>
        </Alert>
      )}

      {/* Header with Breadcrumbs and Actions */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {breadcrumbs.map((item, index) => (
            <div key={item.id || 'root'} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="h-4 w-4" />}
              <button
                onClick={() => handleBreadcrumbClick(index)}
                className="hover:text-foreground transition-colors"
                disabled={index === breadcrumbs.length - 1}
              >
                {item.name}
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {currentFolderId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackClick}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          
          <Dialog open={isCreateFolderOpen} onOpenChange={setIsCreateFolderOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <FolderPlus className="h-4 w-4" />
                New Folder
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Folder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsCreateFolderOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateFolder}
                    disabled={!newFolderName.trim() || createFolderMutation.isPending}
                  >
                    Create
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Upload className="h-4 w-4" />
            Upload Images
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      </div>

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([id, upload]) => (
            <Card key={id} className="p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="truncate flex-1">{upload.file.name}</span>
                <Badge variant={upload.status === 'error' ? 'destructive' : 'default'}>
                  {upload.status === 'uploading' ? `${upload.progress}%` : upload.status}
                </Badge>
              </div>
              {upload.status === 'uploading' && (
                <Progress value={upload.progress} className="mt-2" />
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Gallery Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {/* Folders */}
          {folders?.map((folder) => (
            <Card
              key={folder.id}
              className="group cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleFolderClick(folder)}
            >
              <CardContent className="p-4 aspect-square flex flex-col items-center justify-center relative">
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteFolder(folder.id)
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <Folder className="h-12 w-12 text-primary mb-2" />
                <span className="text-sm font-medium text-center line-clamp-2">
                  {folder.name}
                </span>
              </CardContent>
            </Card>
          ))}

          {/* Images */}
          {images?.map((image) => (
            <Card
              key={image.id}
              className="group cursor-pointer hover:shadow-md transition-shadow"
            >
              <CardContent className="p-2 aspect-square relative">
                <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setSelectedImage(image)}
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => handleDeleteImage(image)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <GalleryImage
                  image={image}
                  onClick={() => setSelectedImage(image)}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 rounded-b-md">
                  <p className="text-xs truncate">{image.name}</p>
                  {image.file_size && (
                    <p className="text-xs text-gray-300">{formatFileSize(image.file_size)}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Empty State */}
          {!folders?.length && !images?.length && (
            <div className="col-span-full text-center py-12">
              <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                No content yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Start by creating folders or uploading images
              </p>
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsCreateFolderOpen(true)}
                  className="flex items-center gap-2"
                >
                  <FolderPlus className="h-4 w-4" />
                  Create Folder
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Upload Images
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <ImagePreviewModal 
          image={selectedImage} 
          onClose={() => setSelectedImage(null)} 
        />
      )}
    </div>
  )
}
